import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  FolderKanban,
  Plus,
  Search,
  Grid3x3,
  List as ListIcon,
  MoreVertical,
  ArrowUpRight,
  Copy,
  Archive,
  ArchiveRestore,
  Trash2,
  SearchX,
  Loader2,
  TriangleAlert,
  ShieldAlert,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { SectionHead, Mantra, MantraAccent } from "@/components/brand/Card";
import { ErrorState } from "@/components/ErrorState";
import { useToast } from "@/hooks/use-toast";
import {
  useProjects,
  useCreateProject,
  useDuplicateProject,
  useUpdateProject,
  useDeleteProject,
  useWorkspaceSettings,
} from "@/hooks/useApi";
import { useAuthStore } from "@/store/authStore";
import { hasMinRole } from "@/lib/roles";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { formatRelative, formatDateTime } from "@/lib/datetime";
import { FOCUS_RING_CLASS, TOUCH_TARGET_CLASS } from "@/lib/a11y";
import type { Project, ProjectListItem } from "@/types/api";
import {
  ProjectSourceFields,
  type ProjectSourceDraft,
} from "@/components/projects/ProjectSourceFields";
import { ClientPicker } from "@/components/projects/ClientPicker";
import { WINDOW_DEFAULT_MONTHS, windowMonthsIsValid } from "@/lib/projectSource";

/** Origem e modo começam vazios de propósito: escolher é obrigatório (#1784). */
const EMPTY_SOURCE_DRAFT: ProjectSourceDraft = {
  source_type: "",
  mode: "",
  window_months: String(WINDOW_DEFAULT_MONTHS),
};

// ───────────────────────── Helpers ─────────────────────────

const PHASE_TONE: Record<string, string> = {
  discover: "bg-[#b2bccb]",
  prepare: "bg-[#596b85]",
  explore: "bg-[#3d6fc7]",
  realize: "bg-[#1d4ea0]",
  deploy: "bg-[#27a155]",
};

const PROGRESS_FILL: Record<string, string> = {
  discover: "linear-gradient(90deg,#b2bccb,#7b8799)",
  prepare: "linear-gradient(90deg,#596b85,#7b8799)",
  explore: "linear-gradient(90deg,#1d4ea0,#3d6fc7)",
  realize: "linear-gradient(90deg,#ffc83d,#ffb300)",
  deploy: "linear-gradient(90deg,#27a155,#3a8a55)",
};

const LOGO_GRADIENTS = [
  "linear-gradient(135deg,#08254c,#1d4ea0)",
  "linear-gradient(135deg,#27a155,#3a8a55)",
  "linear-gradient(135deg,#c25500,#f27325)",
  "linear-gradient(135deg,#1d4ea0,#08254c)",
  "linear-gradient(135deg,#ffb300,#c25500)",
  "linear-gradient(135deg,#ffc83d,#ffb300)",
  "linear-gradient(135deg,#596b85,#08254c)",
  "linear-gradient(135deg,#7b8799,#596b85)",
];

function stableHash(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}
function logoGradient(id: string) {
  return LOGO_GRADIENTS[stableHash(id) % LOGO_GRADIENTS.length];
}
function projectInitials(name: string) {
  return (
    name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "PR"
  );
}
/** Fase e progresso como o Overview mostra (`ProjectDetail` +
 * `ProjectHeaderBanner`): só a fase calculada pelo pipeline, sem cair no
 * `current_phase`, que nasce "discover" e nada atualiza; o progresso é a
 * fração de agentes concluídos. */
function displayPhase(p: ProjectListItem) {
  return p.computed_phase || null;
}
function progressPct(p: ProjectListItem) {
  if (p.agents_total <= 0) return 0;
  return Math.round((p.agents_completed / p.agents_total) * 100);
}
function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function phaseLabel(p: ProjectListItem, t: TFunction) {
  const phase = displayPhase(p);
  if (phase) return capitalize(phase);
  // Sem fase, duas situações diferentes. Nenhum agente concluído é "não
  // começou". Mas o Discover exige o Functional, e nada impede rodar o
  // Architect antes. Aí há agente concluído e fase nenhuma, e dizer "não
  // começou" ao lado de "25%" afirma o contrário do que a tela mediu.
  // Travessão é o que se sabe: nenhuma fase do Activate foi alcançada.
  return p.agents_completed > 0 ? "—" : t("Not started");
}
/** Cor da faixa lateral do card e da barra da lista. Fica no `current_phase`
 * de propósito: é a identidade visual do projeto, não o progresso. */
function phaseTone(p: ProjectListItem) {
  return p.status === "completed" ? "deploy" : p.current_phase?.toLowerCase() || "discover";
}

type StatusFilter = "active" | "archived" | "all";
type ViewMode = "grid" | "list";

// ───────────────────────── Page ─────────────────────────

export default function Projects() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthStore();

  const { data: projects = [], isLoading, isError, refetch } = useProjects();
  // Nullable: sem nome configurado no Admin, o campo Workspace sai do topo.
  const workspaceName = useWorkspaceSettings().data?.workspace_name?.trim();
  const createProject = useCreateProject();
  const deleteProject = useDeleteProject();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>(
    (localStorage.getItem("mygrow-proj-view") as ViewMode) || "grid",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sourceDraft, setSourceDraft] = useState<ProjectSourceDraft>(EMPTY_SOURCE_DRAFT);
  const [clientId, setClientId] = useState("");

  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const { active, archived } = useMemo(() => {
    const q = query.trim().toLowerCase();
    // A fase entra como a tela a mostra: buscar "discover" não pode trazer
    // projeto que o card diz que não começou.
    const matches = (p: ProjectListItem) =>
      !q ||
      `${p.name} ${p.description ?? ""} ${phaseLabel(p, t)}`
        .toLowerCase()
        .includes(q);
    return {
      active: projects
        .filter((p) => p.status !== "archived" && matches(p))
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
      archived: projects
        .filter((p) => p.status === "archived" && matches(p))
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    };
  }, [projects, query, t]);

  const counts = {
    active: projects.filter((p) => p.status !== "archived").length,
    archived: projects.filter((p) => p.status === "archived").length,
    // O admin vê o tenant inteiro; atribuído é só onde ele é membro.
    assigned: projects.filter((p) => p.status !== "archived" && p.is_assigned).length,
  };
  const showActive = statusFilter !== "archived";
  const showArchived = statusFilter !== "active";

  function changeView(mode: ViewMode) {
    setViewMode(mode);
    localStorage.setItem("mygrow-proj-view", mode);
  }

  const sourceReady =
    sourceDraft.source_type !== "" &&
    sourceDraft.mode !== "" &&
    windowMonthsIsValid(Number(sourceDraft.window_months));
  const canCreate =
    name.trim().length > 0 && clientId !== "" && sourceReady && !createProject.isPending;

  async function handleCreate() {
    if (!canCreate || sourceDraft.source_type === "" || sourceDraft.mode === "") return;
    try {
      const project = await createProject.mutateAsync({
        name: name.trim(),
        client_id: clientId,
        description: description.trim() || undefined,
        source_type: sourceDraft.source_type,
        mode: sourceDraft.mode,
        window_months: Number(sourceDraft.window_months),
      });
      trackEvent("project_created", {
        project_id: project.id,
        source: "projects",
      });
      setCreateOpen(false);
      setName("");
      setDescription("");
      setSourceDraft(EMPTY_SOURCE_DRAFT);
      setClientId("");
      navigate(`/projects/${project.id}`);
    } catch (err) {
      toast({ variant: "destructive", title: t("Failed to create project"), description: String(err) });
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    if (deleteConfirmText.trim() !== pendingDelete.name) return;
    const projectName = pendingDelete.name;
    try {
      await deleteProject.mutateAsync(pendingDelete.id);
      toast({ title: `${t("Deleted")} · ${projectName}` });
      setPendingDelete(null);
      setDeleteConfirmText("");
    } catch (err) {
      toast({ variant: "destructive", title: t("Delete failed"), description: String(err) });
    }
  }

  if (isError) {
    return <ErrorState message={t("Failed to load projects")} onRetry={() => refetch()} />;
  }

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<FolderKanban />}
        overline={`${t("Workspace").toUpperCase()} · ${t("Projects").toUpperCase()}`}
        title={t("Projects")}
        miniBadge={`${counts.active} ${t("active")} · ${counts.archived} ${t("Archived").toLowerCase()}`}
        meta={
          <>
            {workspaceName && (
              <>
                <HeroMetaItem k={t("Workspace")} v={workspaceName} />
                <HeroMetaSep />
              </>
            )}
            <HeroMetaItem k={t("Assigned to you")} v={`${counts.assigned} ${t("Projects").toLowerCase()}`} />
          </>
        }
        actions={
          <>
            {/* `POST /api/projects` exige `require_min_role(ADMIN)`
                (`projects.py:101`). Quem não é admin não vê o botão em vez de
                descobrir no 403. */}
            {hasMinRole(user?.role, "admin") && (
              <Button
                variant="action"
                size="sm"
                className="h-9 gap-2 px-3.5 text-xs font-semibold"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="w-3.5 h-3.5" /> {t("New project")}
              </Button>
            )}
          </>
        }
      />

      <div className="p-8 space-y-5">
        {/* Toolbar */}
        <div className="bg-white border border-[#e8ebf0] rounded-xl overflow-hidden shadow-[0_1px_2px_rgba(8,37,76,0.04)]">
          <div className="flex gap-2 items-center px-4 py-3 flex-wrap" data-testid="projects-toolbar">
            <div
              className="h-8 bg-[#f7f7f7] border border-[#e8ebf0] rounded-md flex items-center px-2.5 gap-2 text-xs text-[#7b8799] min-w-[300px]"
              style={{ flex: "0 1 300px" }}
            >
              <Search className="w-3 h-3 shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="border-0 outline-0 bg-transparent flex-1 text-xs text-[#08254c] placeholder:text-[#7b8799] min-w-0"
                placeholder={t("Search by project, client, SAP phase, owner…")}
              />
            </div>
            <Segmented
              value={statusFilter}
              options={[
                { label: t("Active"), value: "active", count: counts.active },
                { label: t("Archived"), value: "archived", count: counts.archived },
                { label: t("All"), value: "all" },
              ]}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
            />
            <div className="flex-1" />
            <Segmented
              value={viewMode}
              options={[
                { label: <Grid3x3 className="w-3.5 h-3.5" />, value: "grid", ariaLabel: t("Grid view") },
                { label: <ListIcon className="w-3.5 h-3.5" />, value: "list", ariaLabel: t("List view") },
              ]}
              onChange={(v) => changeView(v as ViewMode)}
              compact
            />
          </div>
        </div>

        {showActive && (
          <section>
            {/* Dizia "In flight" e "currently being executed by the agent
                pipeline" sobre um agrupamento que é só `status !== "archived"` —
                com todos os projetos em DRAFT, a tela afirmava execução onde não
                havia nenhuma. O título passa a descrever o critério real,
                simétrico ao "Off the active roster" da seção de arquivados. */}
            <SectionHead
              overline={`${t("Active").toUpperCase()} · ${active.length}`}
              title={<span className="text-[18px]">{t("On the active roster")}</span>}
              sub={t("Everything not archived — drafts, running pipelines and finished work.")}
            />

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-56" />
                ))}
              </div>
            ) : active.length === 0 ? (
              <EmptyState
                title={query ? t("No projects match your search") : t("No active projects yet")}
                body={
                  query
                    ? t("Try a different keyword or clear the filters.")
                    : t("Create your first project to kick off an agent pipeline.")
                }
              />
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {active.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    onOpen={() => navigate(`/projects/${p.id}`)}
                    onDelete={() => {
                      setPendingDelete(p);
                      setDeleteConfirmText("");
                    }}
                    toast={toast}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white border border-[#e8ebf0] rounded-xl shadow-[0_1px_2px_rgba(8,37,76,0.04)] overflow-hidden">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: "28%" }}>{t("Project")}</th>
                      <th>{t("Phase")}</th>
                      <th>{t("Progress")}</th>
                      <th>{t("Artifacts")}</th>
                      <th>{t("Runs")}</th>
                      <th>{t("Updated")}</th>
                      <th style={{ width: 30 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {active.map((p) => (
                      <ProjectRow
                        key={p.id}
                        project={p}
                        onOpen={() => navigate(`/projects/${p.id}`)}
                        onDelete={() => {
                          setPendingDelete(p);
                          setDeleteConfirmText("");
                        }}
                        toast={toast}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {showArchived && (
          <section className="mt-8">
            <SectionHead
              overline={`${t("Archived").toUpperCase()} · ${archived.length}`}
              title={<span className="text-[18px]">{t("Off the active roster")}</span>}
              sub={t("Read-only. Restore to continue runs or permanently delete after 90 days.")}
            />
            <div className="bg-white border border-[#e8ebf0] rounded-xl shadow-[0_1px_2px_rgba(8,37,76,0.04)] overflow-hidden">
              {archived.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-[#596b85]">
                  {t("No archived projects.")}
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: "32%" }}>{t("Project")}</th>
                      <th>{t("Description")}</th>
                      <th>{t("Final phase")}</th>
                      <th>{t("Archived")}</th>
                      <th style={{ textAlign: "right" }}>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archived.map((p) => (
                      <ArchivedRow
                        key={p.id}
                        project={p}
                        onDelete={() => {
                          setPendingDelete(p);
                          setDeleteConfirmText("");
                        }}
                        toast={toast}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        <Mantra>
          {projects.length} {t(projects.length === 1 ? "PROJECT" : "PROJECTS")} · <MantraAccent>{t("ONE PLATFORM")}</MantraAccent> · {t("FULL TRACEABILITY")}
        </Mantra>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("New Project")}</DialogTitle>
            <DialogDescription>{t("Create a new SAP migration project")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("Name")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., ACME Corp S/4HANA Migration"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t("Description")}</Label>
              <Textarea
                id="description"
                placeholder={t("Brief summary of the migration scope…")}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <ClientPicker idPrefix="new-project" value={clientId} onChange={setClientId} />
            <ProjectSourceFields
              idPrefix="new-project"
              value={sourceDraft}
              onChange={(next) => setSourceDraft((current) => ({ ...current, ...next }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleCreate} disabled={!canCreate}>
              {createProject.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal — navy ribbon (Direction A) */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setDeleteConfirmText("");
          }
        }}
      >
        <AlertDialogContent className="max-w-[560px] p-0 overflow-hidden gap-0 border-[#e8ebf0] rounded-[14px] shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
          <AlertDialogHeader className="relative bg-[#08254c] text-white px-[22px] pt-[18px] pb-4 flex flex-row gap-3.5 items-start space-y-0 sm:text-left overflow-hidden">
            <span
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 95% -10%, rgba(255,200,61,0.20), transparent 55%)",
              }}
            />
            <div
              className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-[#ffc83d]"
              style={{ background: "rgba(255,200,61,0.14)", border: "1px solid rgba(255,200,61,0.40)" }}
            >
              <TriangleAlert className="w-5 h-5" />
            </div>
            <div className="relative flex-1 min-w-0">
              <h3 className="font-brand font-bold text-[17px] text-white m-0 mb-1.5 tracking-[-0.01em] leading-[1.3]">
                Delete <span className="text-[#ffc83d]">“{pendingDelete?.name}”</span>?
              </h3>
              <div className="text-[13px] text-white/70 leading-[1.55] m-0">
                {t("This will permanently remove the project, all run history, generated artifacts, and SAP connections.")}{" "}
                <strong className="text-white font-semibold">{t("This cannot be undone.")}</strong>
              </div>
            </div>
            <AlertDialogCancel
              className="relative ml-auto mt-0 h-7 w-7 p-0 rounded-md bg-transparent border-0 text-white/55 hover:bg-white/10 hover:text-white shrink-0"
              aria-label={t("Close")}
            >
              <X className="w-3.5 h-3.5" />
            </AlertDialogCancel>
          </AlertDialogHeader>

          <div className="px-[22px] pt-4 pb-[18px] bg-[#fafbfc] border-t border-[#f2f4f7]">
            <div
              className="flex gap-2.5 items-start p-3 rounded-lg text-[12.5px] leading-[1.5]"
              style={{
                background: "#fff6db",
                border: "1px solid rgba(255,200,61,0.55)",
                color: "#6a4a00",
              }}
            >
              <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
              <span>
                {t("Runs, artifacts, and SAP connections attached to this project will be deleted alongside it.")}
              </span>
            </div>
            <div className="mt-3.5">
              <Label className="block text-xs text-[#08254c] mb-1.5 font-semibold">
                Type{" "}
                <span
                  className="font-mono font-bold text-[#08254c] px-1.5 rounded-[3px]"
                  style={{ background: "rgba(255,200,61,0.25)" }}
                >
                  {pendingDelete?.name}
                </span>{" "}
                {t("to confirm")}
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Project name…"
                autoComplete="off"
                className="h-[38px] font-mono text-[13px] text-[#08254c] border-[#d4dae3] focus-visible:ring-[#08254c]/20 focus-visible:ring-[3px] focus-visible:border-[#08254c] focus-visible:ring-offset-0"
              />
              {deleteConfirmText.length > 0 &&
                deleteConfirmText.trim() !== pendingDelete?.name && (
                  <div className="text-[11px] text-[#08254c] font-semibold mt-1.5 font-mono tracking-[0.04em]">
                    {t("Names must match exactly, including spaces and casing.")}
                  </div>
                )}
            </div>
          </div>

          <AlertDialogFooter className="px-[22px] py-3.5 gap-2 border-t border-[#f2f4f7] bg-white flex-row justify-end items-center sm:space-x-0">
            <span className="mr-auto font-mono text-[10.5px] text-[#7b8799] tracking-[0.10em] uppercase flex items-center">
              {t("Irreversible · admin only")}
            </span>
            <AlertDialogCancel
              disabled={deleteProject.isPending}
              className="h-9 mt-0 px-3.5 rounded-md border border-[#d4dae3] bg-white text-[#08254c] text-[13px] font-semibold hover:bg-[#f7f7f7] hover:border-[#08254c]"
            >
              {t("Cancel")}
            </AlertDialogCancel>
            <Button
              onClick={handleDelete}
              disabled={deleteConfirmText.trim() !== pendingDelete?.name || deleteProject.isPending}
              className="h-9 px-3.5 rounded-md bg-[#08254c] border border-[#08254c] text-white text-[13px] font-semibold gap-1.5 shadow-[0_4px_12px_rgba(8,37,76,0.25)] hover:bg-[#0d356e] hover:border-[#0d356e]"
            >
              {deleteProject.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#ffc83d]" />
              ) : (
                <Trash2 className="w-3.5 h-3.5 text-[#ffc83d]" />
              )}
              {t("Delete permanently")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ───────────────────────── Segmented ─────────────────────────

function Segmented<T extends string>({
  value,
  options,
  onChange,
  compact = false,
}: {
  value: T;
  /** `ariaLabel` é obrigatório quando `label` é só um ícone: sem ele o botão
   *  chega ao leitor de tela como "button", sem dizer o que seleciona. */
  options: { label: React.ReactNode; value: T; count?: number; ariaLabel?: string }[];
  onChange: (v: T) => void;
  compact?: boolean;
}) {
  return (
    <div className="inline-flex border border-[#e0e6ed] rounded-lg bg-white overflow-hidden">
      {options.map((opt, i) => {
        const on = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            aria-label={opt.ariaLabel}
            aria-pressed={on}
            onClick={() => onChange(opt.value)}
            className={cn(
              "border-0 bg-transparent font-[inherit] text-xs text-[#596b85] cursor-pointer flex items-center gap-1.5",
              FOCUS_RING_CLASS,
              compact ? "h-[30px] px-2.5" : "h-[34px] px-3.5",
              i > 0 && "border-l border-[#e0e6ed]",
              on && "bg-[#08254c] text-white",
            )}
          >
            <span className="inline-flex items-center gap-1.5">{opt.label}</span>
            {opt.count != null && <span className="font-mono text-[10px] opacity-70">{opt.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────── Grid card ─────────────────────────

function ProjectCard({
  project: p,
  onOpen,
  onDelete,
  toast,
}: {
  project: ProjectListItem;
  onOpen: () => void;
  onDelete: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const { t } = useTranslation();
  const pct = progressPct(p);
  const toneKey = phaseTone(p);

  return (
    <article
      className="relative bg-white border border-[#e8ebf0] rounded-xl p-5 cursor-pointer transition-all duration-200 hover:border-[#08254c] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(8,37,76,0.08)]"
      onClick={onOpen}
    >
      <span
        className={cn(
          "absolute top-0 left-0 w-[3px] h-full rounded-[12px_0_0_12px]",
          PHASE_TONE[toneKey] || "bg-[#b2bccb]",
        )}
      />
      <header className="flex justify-between items-start gap-3 mb-3.5">
        <div className="flex gap-3 items-start min-w-0">
          <div
            className="w-[42px] h-[42px] rounded-[10px] flex items-center justify-center font-brand font-extrabold text-[13px] text-white tracking-[0.04em] shrink-0"
            style={{ background: logoGradient(p.id) }}
          >
            {projectInitials(p.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] text-[#7b8799] tracking-[0.12em] uppercase mb-1 truncate min-h-[14px]">
              {p.description}
            </div>
            <div className="font-brand font-bold text-[15px] text-[#08254c] tracking-[-0.01em] leading-[1.3] truncate flex items-center gap-1.5">
              {p.name}
            </div>
          </div>
        </div>
        <ProjectMenu project={p} onOpen={onOpen} onDelete={onDelete} toast={toast} />
      </header>

      <div className="flex justify-between font-mono text-[9px] text-[#7b8799] tracking-[0.12em] uppercase mb-1.5">
        <span>Phase</span>
        <span className="text-[#08254c] font-semibold">
          {phaseLabel(p, t)} · {pct}%
        </span>
      </div>
      {/* Uma fatia por agente do pipeline, acesa quando ele conclui: mede o
          mesmo que o % ao lado. */}
      <div
        data-testid="agents-bar"
        className="grid gap-[3px] mb-3"
        style={{ gridTemplateColumns: `repeat(${Math.max(p.agents_total, 1)}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: p.agents_total }, (_, i) => {
          const cls =
            i < p.agents_completed ? "bg-gradient-to-r from-[#ffc83d] to-[#ffb300]" : "bg-[#e8ebf0]";
          return <div key={i} className={cn("h-1 rounded-sm", cls)} />;
        })}
      </div>

      <div className="grid grid-cols-2 gap-2.5 pt-3 border-t border-[#f2f4f7]">
        <KpiCell k="Status" v={capitalize(p.status)} />
        <KpiCell k="Updated" v={formatRelative(p.updated_at)} title={formatDateTime(p.updated_at)} mono />
      </div>
    </article>
  );
}

function KpiCell({ k, v, mono, title }: { k: string; v: string; mono?: boolean; title?: string }) {
  return (
    <div>
      <div className="font-mono text-[9px] text-[#7b8799] tracking-[0.1em] uppercase">{k}</div>
      <div
        title={title}
        className={cn(
          "font-brand font-bold text-[14px] text-[#08254c] mt-0.5 tracking-[-0.01em]",
          mono && "font-mono font-medium text-[11px]",
        )}
      >
        {v}
      </div>
    </div>
  );
}

// ───────────────────────── List view row ─────────────────────────

function ProjectRow({
  project: p,
  onOpen,
  onDelete,
  toast,
}: {
  project: ProjectListItem;
  onOpen: () => void;
  onDelete: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const { t } = useTranslation();
  const pct = progressPct(p);
  const toneKey = phaseTone(p);

  return (
    <tr onClick={onOpen} style={{ cursor: "pointer" }}>
      <td>
        <div className="flex items-center gap-3">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center font-brand font-bold text-[11px] text-white tracking-[-0.02em] shrink-0"
            style={{ background: logoGradient(p.id) }}
          >
            {projectInitials(p.name)}
          </span>
          <div>
            <div className="font-semibold text-[13px] text-[#08254c]">{p.name}</div>
            {p.description && (
              <div className="font-mono text-[10px] text-[#7b8799] tracking-[0.1em] uppercase mt-0.5">
                {p.description}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="font-mono text-[11px] text-[#08254c] font-semibold">
        {phaseLabel(p, t)}
      </td>
      <td>
        <div className="flex items-center gap-2.5 min-w-[160px]">
          <div className="flex-1 h-1.5 rounded-[3px] bg-[#e8ebf0] overflow-hidden">
            <span
              className="block h-full rounded-[3px]"
              style={{ width: `${pct}%`, background: PROGRESS_FILL[toneKey] || "linear-gradient(90deg,#ffc83d,#ffb300)" }}
            />
          </div>
          <div className="font-mono text-[11px] text-[#08254c] font-semibold min-w-[30px] text-right">{pct}%</div>
        </div>
      </td>
      <td className="mono">{p.artifact_count}</td>
      <td className="mono">{p.run_count}</td>
      <td className="font-mono text-[11px]" title={formatDateTime(p.updated_at)}>
        {formatRelative(p.updated_at)}
      </td>
      <td>
        <ProjectMenu project={p} onOpen={onOpen} onDelete={onDelete} toast={toast} />
      </td>
    </tr>
  );
}

// ───────────────────────── Archived row ─────────────────────────

function ArchivedRow({
  project: p,
  onDelete,
  toast,
}: {
  project: ProjectListItem;
  onDelete: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const updateProject = useUpdateProject(p.id);

  async function handleRestore() {
    try {
      await updateProject.mutateAsync({ status: "draft" });
      toast({ title: `${t("Restored")} · ${p.name}` });
    } catch (err) {
      toast({ variant: "destructive", title: t("Restore failed"), description: String(err) });
    }
  }

  return (
    <tr>
      <td>
        <div className="flex items-center gap-2.5 text-[#08254c] font-semibold">
          <span
            className="w-7 h-7 rounded-md flex items-center justify-center font-brand font-bold text-[10px] text-white tracking-[-0.02em] shrink-0"
            style={{ background: logoGradient(p.id) }}
          >
            {projectInitials(p.name)}
          </span>
          {p.name}
        </div>
      </td>
      <td className="font-mono text-[11px]">{p.description || "—"}</td>
      <td className="font-mono text-[11px]">{phaseLabel(p, t)}</td>
      <td className="font-mono text-[11px]" title={formatDateTime(p.updated_at)}>
        {formatRelative(p.updated_at)}
      </td>
      <td style={{ textAlign: "right" }}>
        <div className="inline-flex gap-1.5">
          {/* Restaurar é um `PATCH`, e o `PATCH` é `require_project_access(MANAGER)`
              — o mesmo corte do menu da linha ativa. */}
          {hasMinRole(user?.role, "manager") && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRestore();
              }}
              className="h-7 px-2.5 rounded-md border border-[#e8ebf0] bg-white text-[#596b85] font-mono text-[11px] tracking-[0.04em] inline-flex items-center gap-1.5 cursor-pointer hover:border-[#08254c] hover:text-[#08254c] transition-colors"
            >
              <ArchiveRestore className="w-3 h-3" /> {t("Restore")}
            </button>
          )}
          {hasMinRole(user?.role, "admin") && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="h-7 px-2.5 rounded-md border border-[#e8ebf0] bg-white text-[#d4353c] font-mono text-[11px] tracking-[0.04em] inline-flex items-center gap-1.5 cursor-pointer hover:border-[#d4353c] transition-colors"
            >
              <Trash2 className="w-3 h-3" /> {t("Delete")}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ───────────────────────── Actions menu ─────────────────────────

function ProjectMenu({
  project: p,
  onOpen,
  onDelete,
  toast,
}: {
  project: Project;
  onOpen: () => void;
  onDelete: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const updateProject = useUpdateProject(p.id);
  const navigate = useNavigate();
  const duplicate = useDuplicateProject();
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState("");

  function openDuplicate() {
    setDuplicateName(`${p.name} (copy)`);
    setDuplicateOpen(true);
  }

  async function handleDuplicate() {
    try {
      const name = duplicateName.trim();
      const clone = await duplicate.mutateAsync({
        projectId: p.id,
        name: name || undefined,
      });
      setDuplicateOpen(false);
      toast({ title: `Duplicated · ${clone.name}` });
      navigate(`/projects/${clone.id}`);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Duplicate failed",
        description: String(err),
      });
    }
  }

  async function archive() {
    try {
      await updateProject.mutateAsync({ status: "archived" });
      toast({ title: `${t("Archived")} · ${p.name}` });
    } catch (err) {
      toast({ variant: "destructive", title: t("Archive failed"), description: String(err) });
    }
  }

  async function restore() {
    try {
      await updateProject.mutateAsync({ status: "draft" });
      toast({ title: `${t("Restored")} · ${p.name}` });
    } catch (err) {
      toast({ variant: "destructive", title: t("Restore failed"), description: String(err) });
    }
  }

  const isArchived = p.status === "archived";

  return (
    <>
    <DropdownMenu>
      {/* Sem `aria-label` o Radix renderizava um `<button>` cujo único conteúdo
          era o `<svg>`: um leitor de tela anunciava "button" e nada mais. Nomeia
          o projeto, como a tela de Team já fazia com a pessoa da linha. */}
      <DropdownMenuTrigger
        aria-label={t("Actions for {{name}}", { name: p.name })}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "w-7 h-7 rounded-md border border-transparent bg-transparent cursor-pointer text-[#7b8799] flex items-center justify-center hover:border-[#e0e6ed] hover:bg-[#f7f7f7] hover:text-[#08254c]",
          TOUCH_TARGET_CLASS,
          FOCUS_RING_CLASS,
        )}
      >
        <MoreVertical className="w-[15px] h-[15px]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          <ArrowUpRight className="mr-2 h-3.5 w-3.5" /> {t("Open project")}
        </DropdownMenuItem>
        {/* `require_project_access(ADMIN)` (`projects.py:154`). O eixo de
            alcance não precisa ser checado aqui: a lista já vem filtrada por
            atribuição, então um projeto visível é um projeto alcançável. */}
        {hasMinRole(user?.role, "admin") && (
          <DropdownMenuItem
            disabled={duplicate.isPending}
            onClick={(e) => {
              e.stopPropagation();
              openDuplicate();
            }}
          >
            <Copy className="mr-2 h-3.5 w-3.5" /> {t("Duplicate")}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {/* `PATCH /api/projects/{id}` exige `require_project_access(MANAGER)`
            (`projects.py:301`) — arquivar e restaurar passam por lá. */}
        {hasMinRole(user?.role, "manager") &&
          (isArchived ? (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                restore();
              }}
            >
              <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> {t("Restore")}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                archive();
              }}
            >
              <Archive className="mr-2 h-3.5 w-3.5" /> {t("Archive")}
            </DropdownMenuItem>
          ))}
        {hasMinRole(user?.role, "admin") && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-[#d4353c] focus:text-[#d4353c]"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" /> {t("Delete permanently")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Duplicate project</DialogTitle>
          <DialogDescription>
            Creates a fresh Draft with the base config and the SAP connection
            replicated. Runs, decisions and approval are not copied.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-2">
          <Label htmlFor="duplicate-name">New project name</Label>
          <Input
            id="duplicate-name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleDuplicate();
            }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDuplicateOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={duplicate.isPending || !duplicateName.trim()}
          >
            {duplicate.isPending ? "Duplicating…" : "Duplicate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ───────────────────────── Empty state ─────────────────────────

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="py-14 px-5 text-center text-[#7b8799] bg-white border border-[#e8ebf0] rounded-xl">
      <div className="w-16 h-16 rounded-full bg-[#f2f4f7] flex items-center justify-center mx-auto mb-3.5 text-[#b2bccb]">
        <SearchX className="w-6 h-6" />
      </div>
      <h4 className="font-brand font-bold text-[#08254c] m-0 mb-1 text-[15px]">{title}</h4>
      <p className="text-[13px] m-0">{body}</p>
    </div>
  );
}
